import React, { useEffect, useState } from 'react';
import { Table, Form, Button, Spinner, Modal, Toast, ToastContainer } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  getAllStaffData,
  updateAccountStatus,
  updateAccountDetails,
} from '../../accountService.js';
import './AccountListPage.scss';
import { ROLE_TYPES, BUSINESS_LINE_TYPES, REGION_TYPES } from '@/constants/constants.js';
import PageContentLayout from '@/components/layout/PageContentLayout/PageContentLayout.js';
import {
  SYSTEM_MANAGEMENT_SECTIONS,
  getSystemManagementSectionPath,
} from '@/features/systemManagement/routes/systemManagementNavigation.js';
import {
  buildAccountEditPayload,
  validateAccountEditForm,
} from './AccountEditForm.js';

const AccountListPage = () => {
  const navigate = useNavigate();
  const [roleFilter, setRoleFilter] = useState(1);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingIds, setUpdatingIds] = useState([]); // track which user ids are being updated

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  const roleMap = Object.fromEntries(
    Object.entries(ROLE_TYPES).map(([label, value]) => [Number(value), label])
  );

  // Format a date string/number into `YYYY-MM-DD HH:mm`
  const formatDateShort = (raw) => {
    if (raw === null || raw === undefined || raw === '') return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    const YYYY = d.getFullYear();
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const DD = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${YYYY}-${MM}-${DD} ${hh}:${mm}`;
  };

  const fetchAccounts = async (role) => {
    try {
      setLoading(true);
      setError('');
      // backend expects a role id path param; use 0 for all
      const param = role;
      const data = await getAllStaffData(param);
      setAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('获取账户列表失败', err);
      setError('获取账户列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts(roleFilter);
  }, [roleFilter]);

  // sorting state: start with no sort (null) so data shows as-is; user can click to sort
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'

  const toggleSort = (key) => {
    if (sortBy === key) {
      // cycle through: asc -> desc -> null (cancel sort)
      if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        // desc -> cancel
        setSortBy(null);
        setSortDir('desc');
      }
    } else {
      // clicking a new column: start with asc
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const parseDateToTs = (raw) => {
    if (!raw && raw !== 0) return 0;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return 0;
    return d.getTime();
  };

  const sortedAccounts = React.useMemo(() => {
    if (!Array.isArray(accounts)) return [];
    const arr = [...accounts];

    // only apply sorting if sortBy is set (user clicked a column header)
    if (!sortBy) return arr;

    if (sortBy === 'created_time') {
      arr.sort((a, b) => {
        const ta = parseDateToTs(
          a.created_time ?? a.createdAt ?? a.create_time ?? ''
        );
        const tb = parseDateToTs(
          b.created_time ?? b.createdAt ?? b.create_time ?? ''
        );
        return sortDir === 'asc' ? ta - tb : tb - ta;
      });
    } else if (sortBy === 'businessLine') {
      arr.sort((a, b) => {
        const va = (a.businessLine ?? a.section ?? '') || '';
        const vb = (b.businessLine ?? b.section ?? '') || '';
        const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    } else if (sortBy === 'region') {
      arr.sort((a, b) => {
        const va = (a.region ?? '') || '';
        const vb = (b.region ?? '') || '';
        const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    } else if (sortBy === 'company_alias') {
      arr.sort((a, b) => {
        const va = (a.company_alias ?? a.alias ?? '') || '';
        const vb = (b.company_alias ?? b.alias ?? '') || '';
        const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    } else if (sortBy === 'parkCode') {
      arr.sort((a, b) => {
        const va = (a.parkCode ?? a.gzhCode ?? '') || '';
        const vb = (b.parkCode ?? b.gzhCode ?? '') || '';
        const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return arr;
  }, [accounts, sortBy, sortDir]);

  const isUpdating = (id) => updatingIds.includes(id);

const handleToggleActive = async (acct) => {
  const id = acct.id ?? acct.userid ?? acct.user_id;
  if (!id) return;

  // normalize current to numeric 0/1 when possible
  const currentRaw = acct.isactive ?? acct.is_active ?? acct.active ?? 0;
  const currentNum = typeof currentRaw === 'number' ? Number(currentRaw) : currentRaw ? 1 : 0;
  const newValNum = currentNum === 1 ? 0 : 1; // toggle between 1 and 0

  try {
    setUpdatingIds((s) => [...s, id]);
    // send 0/1 as requested by backend
    const resp = await updateAccountStatus(id, newValNum);
    if (resp && resp.success) {
      // update local state (store numeric 0/1)
      setAccounts((prev) =>
        prev.map((p) => {
          const pid = p.id ?? p.userid ?? p.user_id;
          if (pid === id) {
            return { ...p, isactive: newValNum };
          }
          return p;
        })
      );
    } else {
      console.error('更新账户状态失败', resp?.error || resp?.message);
    }
  } catch (error) {
    console.error('更新账户状态异常', error);
  } finally {
    setUpdatingIds((s) => s.filter((x) => x !== id));
  }
};

  const handleEditClick = (account) => {
    setEditingAccount({ ...account, newPassword: '' });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const id = editingAccount.id ?? editingAccount.userid ?? editingAccount.user_id;
      const validationError = validateAccountEditForm(editingAccount);
      if (validationError) {
        setToastVariant('danger');
        setToastMessage(validationError);
        setShowToast(true);
        return;
      }

      const payload = buildAccountEditPayload(editingAccount);
      
      const resp = await updateAccountDetails(id, payload);
      if (resp && resp.data && resp.data.success) {
        setToastVariant('success');
        setToastMessage('更新成功');
        setShowToast(true);
        setShowEditModal(false);
        fetchAccounts(roleFilter);
      } else {
        setToastVariant('danger');
        setToastMessage('更新失败：' + (resp?.data?.message || '未知错误'));
        setShowToast(true);
      }
    } catch (error) {
      console.error('更新异常', error);
      setToastVariant('danger');
      setToastMessage('更新异常');
      setShowToast(true);
    }
  };

  return (
    <>
      <PageContentLayout
        pageClassName="accounts-page"
        fill
        title="账户列表"
        actions={
          <Button
            variant="primary"
            onClick={() =>
              navigate(
                getSystemManagementSectionPath(
                  SYSTEM_MANAGEMENT_SECTIONS.ADD_ACCOUNT,
                ),
              )
            }
          >
            添加
          </Button>
        }
        filters={
          <Form.Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(Number(e.target.value))}
            aria-label="角色"
          >
            {Object.entries(roleMap).map(([num, label]) => (
              <option key={num} value={num}>
                {label}
              </option>
            ))}
          </Form.Select>
        }
        bodyClassName="accounts-table-container"
      >
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : (
            <Table responsive bordered hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>账号</th>
                  <th>名称</th>

                  {/* If filtering by 客服 show extra columns */}
                  {roleFilter ===ROLE_TYPES.客服 && (
                    <>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => toggleSort('businessLine')}
                        title="按板块排序"
                      >
                        板块
                        {sortBy === 'businessLine'
                          ? sortDir === 'asc'
                            ? ' ▲'
                            : ' ▼'
                          : ' ⇅'}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => toggleSort('region')}
                        title="按区域排序"
                      >
                        区域
                        {sortBy === 'region'
                          ? sortDir === 'asc'
                            ? ' ▲'
                            : ' ▼'
                          : ' ⇅'}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => toggleSort('company_alias')}
                        title="按公司别称排序"
                      >
                        公司别称
                        {sortBy === 'company_alias'
                          ? sortDir === 'asc'
                            ? ' ▲'
                            : ' ▼'
                          : ' ⇅'}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => toggleSort('parkCode')}
                        title="按园区编码排序"
                      >
                        园区编码
                        {sortBy === 'parkCode'
                          ? sortDir === 'asc'
                            ? ' ▲'
                            : ' ▼'
                          : ' ⇅'}
                      </th>
                      <th>
                        收费标准
                      </th>
                    </>
                  )}

                  <th
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => toggleSort('created_time')}
                    title="按创建时间排序"
                  >
                    创建时间
                    {sortBy === 'created_time'
                      ? sortDir === 'asc'
                        ? ' ▲'
                        : ' ▼'
                      : ' ⇅'}
                  </th>
                  <th
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="状态"
                  >
                    状态
                  </th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {sortedAccounts.length === 0 ? (
                  <tr>
                    <td
                      colSpan={roleFilter === ROLE_TYPES.客服 ? 11 : 6}
                      className="text-center"
                    >
                      暂无数据
                    </td>
                  </tr>
                ) : (
                  sortedAccounts.map((a, idx) => (
                    <tr key={a.id || a.userid || idx}>
                      <td>{idx + 1}</td>
                      <td>{a.name ?? a.username ?? a.account}</td>
                      <td>
                        {a.clientName ?? a.companyName ?? a.company_name ?? ''}
                      </td>

                      {roleFilter ===ROLE_TYPES.客服 ? (
                        <>
                          <td>{a.businessLine ?? a.section ?? ''}</td>
                          <td>{a.region ?? ''}</td>
                          <td>{a.company_alias ?? a.alias ?? ''}</td>
                          <td>{a.parkCode ?? a.gzhCode ?? ''}</td>
                          <td>{a.charge_standard ?? ''}</td>
                        </>
                      ) : null}
                      <td>
                        {formatDateShort(
                          a.created_time ?? a.createdAt ?? a.create_time ?? ''
                        )}
                      </td>

                      {/* 状态列：是否激活 */}
                      <td className="text-center align-middle">
                        {isUpdating(a.id ?? a.userid ?? a.user_id) ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <Form.Check
                            type="switch"
                            id={`active-switch-${
                              a.id ?? a.userid ?? a.user_id
                            }`}
                           checked={Boolean(Number(a.isactive ?? a.is_active ?? a.active ?? 0))}
                            onChange={() => handleToggleActive(a)}
                            label={
                              a.isactive ?? a.is_active ?? a.active
                                ? '启用'
                                : '禁用'
                            }
                          />
                        )}
                      </td>
                      <td className="text-center align-middle">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditClick(a)}
                        >
                          编辑
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
      </PageContentLayout>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>编辑账户</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingAccount && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>名称</Form.Label>
                <Form.Control
                  type="text"
                  value={editingAccount.clientName ?? editingAccount.companyName ?? editingAccount.company_name ?? ''}
                  onChange={(e) => setEditingAccount({ ...editingAccount, companyName: e.target.value, clientName: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>新密码</Form.Label>
                <Form.Control
                  type="password"
                  autoComplete="new-password"
                  value={editingAccount.newPassword ?? ''}
                  onChange={(e) => setEditingAccount({ ...editingAccount, newPassword: e.target.value })}
                  placeholder="不填写则不修改密码"
                />
                <Form.Text className="text-muted">
                  不填写则保持原密码不变。
                </Form.Text>
              </Form.Group>
              
              {roleFilter === ROLE_TYPES.客服 && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>板块</Form.Label>
                    <Form.Select
                      value={editingAccount.businessLine ?? editingAccount.section ?? ''}
                      onChange={(e) => setEditingAccount({ ...editingAccount, businessLine: e.target.value })}
                    >
                      <option value="">请选择</option>
                      {Object.entries(BUSINESS_LINE_TYPES).map(([label, value]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>区域</Form.Label>
                    <Form.Select
                      value={editingAccount.region ?? ''}
                      onChange={(e) => setEditingAccount({ ...editingAccount, region: e.target.value })}
                    >
                      <option value="">请选择</option>
                      {Object.entries(REGION_TYPES).map(([label, value]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>公司别称</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingAccount.company_alias ?? editingAccount.alias ?? ''}
                      onChange={(e) => setEditingAccount({ ...editingAccount, company_alias: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>园区编码</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingAccount.parkCode ?? editingAccount.gzhCode ?? ''}
                      onChange={(e) => setEditingAccount({ ...editingAccount, parkCode: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>收费标准</Form.Label>
                    <Form.Select
                      value={editingAccount.charge_standard ?? ''}
                      onChange={(e) => setEditingAccount({ ...editingAccount, charge_standard: e.target.value })}
                    >
                      <option value="">请选择</option>
                      {Object.entries(BUSINESS_LINE_TYPES).map(([label, value]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSaveEdit}>
            保存
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3000}
          autohide
          bg={toastVariant === 'success' ? 'success' : 'danger'}
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

    </>
  );
};

export default AccountListPage;
